import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { X, ArrowLeft, Search, AlertCircle, Sparkles, Image as ImageIcon, Grid, Users, ThumbsUp, ThumbsDown, User } from 'lucide-react'
import { RootState } from '@/store/store'
import { clearSearch, hideResults, showResults } from '@/store/slices/searchSlice'
import { openImageModal } from '@/store/slices/uiSlice'
import { useSearchFeedbackMutation } from '@/store/api'
import type { SearchResult } from '@/store/api'
import { previewUrl } from '@/utils/media'
import { useSignedUrls } from '@/utils/useSignedUrls'

interface SearchResultCardProps {
  result: SearchResult
  onSelect: (masterId: string) => void
  rank: number
  onFeedback?: (masterId: string, relevant: boolean) => void
}

function SearchResultCard({ result, onSelect, rank, onFeedback }: SearchResultCardProps) {
  const similarityColor =
    result.similarity_percent >= 90 ? 'bg-green-500' :
    result.similarity_percent >= 70 ? 'bg-yellow-500' :
    result.similarity_percent >= 50 ? 'bg-orange-500' :
    'bg-red-500'

  return (
    <div
      onClick={() => onSelect(result.master_id)}
      className="group relative bg-card border border-border rounded-lg overflow-hidden cursor-pointer hover:border-primary hover:shadow-lg transition-all"
    >
      {/* Rank Badge */}
      <div className="absolute top-2 left-2 z-10 w-6 h-6 flex items-center justify-center bg-black/70 text-white text-xs font-medium rounded-full">
        {rank}
      </div>

      {/* Similarity Badge */}
      <div className="absolute top-2 right-2 z-10">
        <div className={`flex items-center gap-1 px-2 py-0.5 ${similarityColor} text-white text-xs font-medium rounded-full`}>
          {result.similarity_percent}%
        </div>
      </div>

      {/* Image */}
      <div className="aspect-square relative">
        <img
          src={previewUrl(result.master_id)}
          alt={result.caption || result.file_name}
          loading="lazy"
          className="w-full h-full object-cover"
          onError={(e) => {
            (e.target as HTMLImageElement).src = '/placeholder.svg'
          }}
        />

        {/* Hover Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="absolute bottom-0 left-0 right-0 p-3">
            {result.caption && (
              <p className="text-white text-sm line-clamp-2 mb-1">{result.caption}</p>
            )}
            <p className="text-white/70 text-xs truncate">{result.file_name}</p>
          </div>
        </div>
      </div>

      {/* Classification Badge (if not photo) */}
      {result.classification && !result.classification.is_photo && (
        <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-black/70 text-white text-xs rounded">
          {result.classification.is_screenshot ? 'Screenshot' : 'Document'}
        </div>
      )}

      {/* Feedback buttons */}
      {onFeedback && (
        <div className="absolute bottom-2 right-2 z-10 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => { e.stopPropagation(); onFeedback(result.master_id, true) }}
            className="p-1 rounded bg-green-600/80 hover:bg-green-600 text-white"
            title="Relevant"
          >
            <ThumbsUp size={12} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onFeedback(result.master_id, false) }}
            className="p-1 rounded bg-red-600/80 hover:bg-red-600 text-white"
            title="Not relevant"
          >
            <ThumbsDown size={12} />
          </button>
        </div>
      )}
    </div>
  )
}

function SearchResultsPanel() {
  useSignedUrls()
  const dispatch = useDispatch()
  const {
    mode,
    isSearching,
    isSearchActive,
    textQuery,
    sourceImageId,
    regionSourceImageId,
    sourceFaceCropUrl,
    sourcePersonId,
    sourcePersonName,
    results,
    error,
    showResultsPanel
  } = useSelector((state: RootState) => state.search)

  const [sendFeedback] = useSearchFeedbackMutation()

  const handleClose = useCallback(() => {
    dispatch(clearSearch())
  }, [dispatch])

  const handleMinimize = useCallback(() => {
    dispatch(hideResults())
  }, [dispatch])

  const handleRestore = useCallback(() => {
    dispatch(showResults())
  }, [dispatch])

  const handleResultSelect = useCallback((masterId: string) => {
    const resultIds = results.map(r => r.master_id)
    dispatch(openImageModal({ masterId, imageList: resultIds }))
  }, [dispatch, results])

  const handleFeedback = useCallback((masterId: string, relevant: boolean) => {
    const queryContext = mode === 'text' ? `text:${textQuery}` :
      mode === 'face' ? `face:${sourceFaceCropUrl}` :
      mode === 'person' ? `person:${sourcePersonId}` :
      mode === 'image' ? `image:${sourceImageId}` :
      `region:${regionSourceImageId}`
    sendFeedback({ queryContext, masterId, relevant })
  }, [mode, textQuery, sourceFaceCropUrl, sourcePersonId, sourceImageId, regionSourceImageId, sendFeedback])

  // If search is not active, don't render
  if (!isSearchActive) return null

  // Minimized state - show floating badge
  if (!showResultsPanel) {
    return (
      <button
        onClick={handleRestore}
        className="fixed bottom-20 right-4 z-40 flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-full shadow-lg hover:bg-primary/90 transition-colors"
      >
        <Search size={16} />
        <span>{results.length} results</span>
      </button>
    )
  }

  // Get search context info
  const getSearchTitle = () => {
    switch (mode) {
      case 'text':
        return `"${textQuery}"`
      case 'image':
        return 'Similar Images'
      case 'region':
        return 'Region Search'
      case 'face':
        return 'Similar Faces'
      case 'person':
        return sourcePersonName ? `Images of ${sourcePersonName}` : 'Person Search'
      default:
        return 'Search Results'
    }
  }

  const getSearchIcon = () => {
    switch (mode) {
      case 'text':
        return <Sparkles size={18} />
      case 'image':
        return <ImageIcon size={18} />
      case 'region':
        return <Grid size={18} />
      case 'face':
        return <Users size={18} />
      case 'person':
        return <User size={18} />
      default:
        return <Search size={18} />
    }
  }

  return (
    <div className="fixed inset-y-0 right-0 z-40 w-full sm:w-96 md:w-[450px] bg-background border-l border-border shadow-xl flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border bg-card">
        <div className="flex items-center gap-3">
          <button
            onClick={handleClose}
            className="p-1.5 hover:bg-secondary rounded-md transition-colors"
            aria-label="Back to library"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 bg-primary/10 text-primary rounded-lg">
              {getSearchIcon()}
            </div>
            <div>
              <div className="text-sm font-medium truncate max-w-48">
                {getSearchTitle()}
              </div>
              <div className="text-xs text-muted-foreground">
                {isSearching ? 'Searching...' : `${results.length} results found`}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={handleMinimize}
            className="p-1.5 hover:bg-secondary rounded-md transition-colors"
            aria-label="Minimize"
          >
            <div className="w-4 h-0.5 bg-current" />
          </button>
          <button
            onClick={handleClose}
            className="p-1.5 hover:bg-secondary rounded-md transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Source Image Preview (for image/region search) */}
      {(mode === 'image' || mode === 'region') && (sourceImageId || regionSourceImageId) && (
        <div className="p-3 border-b border-border bg-secondary/30">
          <div className="text-xs text-muted-foreground mb-2">
            {mode === 'image' ? 'Finding similar to:' : 'Searching region from:'}
          </div>
          <div className="flex items-center gap-3">
            <img
              src={previewUrl(sourceImageId || regionSourceImageId || '')}
              alt="Source"
              className="w-16 h-16 object-cover rounded-lg border border-border"
            />
            <div className="text-sm text-muted-foreground">
              {mode === 'region' && 'Selected region'}
            </div>
          </div>
        </div>
      )}

      {/* Source Face Preview (for face search) */}
      {mode === 'face' && sourceFaceCropUrl && (
        <div className="p-3 border-b border-border bg-secondary/30">
          <div className="text-xs text-muted-foreground mb-2">
            Finding faces similar to:
          </div>
          <div className="flex items-center gap-3">
            <img
              src={sourceFaceCropUrl}
              alt="Source face"
              className="w-16 h-16 object-cover rounded-full border-2 border-cyan-500"
            />
          </div>
        </div>
      )}

      {/* Results Content */}
      <div className="flex-1 overflow-auto p-4">
        {/* Loading State */}
        {isSearching && (
          <div className="flex flex-col items-center justify-center h-64 gap-4">
            <div className="relative">
              <div className="w-12 h-12 border-2 border-primary/20 rounded-full" />
              <div className="absolute inset-0 w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
            <div className="text-sm text-muted-foreground">
              Searching with AI...
            </div>
          </div>
        )}

        {/* Error State */}
        {error && !isSearching && (
          <div className="flex flex-col items-center justify-center h-64 gap-4 text-center">
            <div className={`w-12 h-12 flex items-center justify-center rounded-full ${
              mode === 'face' ? 'bg-secondary text-muted-foreground' : 'bg-destructive/10 text-destructive'
            }`}>
              {mode === 'face' ? <Users size={24} /> : <AlertCircle size={24} />}
            </div>
            <div>
              <div className={`text-sm font-medium mb-1 ${mode === 'face' ? '' : 'text-destructive'}`}>
                {error}
              </div>
              {mode !== 'face' && (
                <div className="text-xs text-muted-foreground">
                  Check that the required services are running
                </div>
              )}
            </div>
            <button
              onClick={handleClose}
              className="text-sm text-primary hover:underline"
            >
              Back to library
            </button>
          </div>
        )}

        {/* Empty State */}
        {!isSearching && !error && results.length === 0 && (
          <div className="flex flex-col items-center justify-center h-64 gap-4 text-center">
            <div className="w-12 h-12 flex items-center justify-center bg-secondary text-muted-foreground rounded-full">
              {mode === 'face' ? <Users size={24} /> : <Search size={24} />}
            </div>
            <div>
              <div className="text-sm font-medium mb-1">
                {mode === 'face' ? 'No similar faces found' : 'No results found'}
              </div>
              <div className="text-xs text-muted-foreground">
                {mode === 'face'
                  ? 'This face was not matched in any other images'
                  : mode === 'text'
                    ? 'Try a different search term or adjust your query'
                    : 'Try searching with a different image or region'}
              </div>
            </div>
          </div>
        )}

        {/* Results Grid */}
        {!isSearching && !error && results.length > 0 && (
          <div className="grid grid-cols-2 gap-3">
            {results.map((result, index) => (
              <SearchResultCard
                key={result.master_id}
                result={result}
                onSelect={handleResultSelect}
                rank={index + 1}
                onFeedback={handleFeedback}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer with Actions */}
      {!isSearching && results.length > 0 && (
        <div className="p-3 border-t border-border bg-card">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Click an image to view details</span>
            <button
              onClick={handleClose}
              className="text-primary hover:underline"
            >
              Clear search
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default SearchResultsPanel
